import React, { createContext, useState, useEffect, useContext } from 'react';
import { AuthContext } from '../AuthContext';
import Notification from '../components/Notification';
import { API_BASE_URL } from '../config';

// DJB-161..165: data layer for the Configuration page. Payment modes and
// long-track price modes list as headers only ({id, name}); the entries live
// behind the per-id endpoint, so the fetchers expand every header into the
// full mode. Bonus configurations and disk policies come back complete.
// DJB-218: the price-sheet PNG of a payment mode is not part of the DTO; it
// sits in S3 under a key derived from the mode id (see Configuration/priceSheet.js).
// It is uploaded (and replaced) as a multipart `file` part to
// POST /paymentmodes/{id}/pricesheet, which answers 204; there is no removal —
// a mode always keeps a sheet and the backend cleans up when the mode is
// deleted. `priceSheetVersions` records the uploads of this session per mode
// id (timestamp, doubles as cache buster); absent when unknown.
export const ConfigurationContext = createContext({
  paymentModes: [],
  paymentModesLoading: false,
  priceSheetVersions: {},
  bonusConfigurations: [],
  bonusConfigurationsLoading: false,
  longTrackModes: [],
  longTrackModesLoading: false,
  diskPolicies: [],
  diskPoliciesLoading: false,
  fetchPaymentModes: async () => {},
  createPaymentMode: async () => {},
  deletePaymentMode: async () => {},
  uploadPriceSheet: async () => {},
  fetchBonusConfigurations: async () => {},
  createBonusConfiguration: async () => {},
  updateBonusConfiguration: async () => {},
  deleteBonusConfiguration: async () => {},
  fetchLongTrackModes: async () => {},
  createLongTrackMode: async () => {},
  deleteLongTrackMode: async () => {},
  fetchDiskPolicies: async () => {},
  createDiskPolicy: async () => {},
  updateDiskPolicy: async () => {},
  deleteDiskPolicy: async () => {},
});

const byName = (a, b) => (a.name || '').localeCompare(b.name || '');

const ConfigurationProvider = ({ children }) => {
  const { authData } = useContext(AuthContext) || {};
  const [paymentModes, setPaymentModes] = useState([]);
  const [paymentModesLoading, setPaymentModesLoading] = useState(false);
  const [priceSheetVersions, setPriceSheetVersions] = useState({});
  const [bonusConfigurations, setBonusConfigurations] = useState([]);
  const [bonusConfigurationsLoading, setBonusConfigurationsLoading] = useState(false);
  const [longTrackModes, setLongTrackModes] = useState([]);
  const [longTrackModesLoading, setLongTrackModesLoading] = useState(false);
  const [diskPolicies, setDiskPolicies] = useState([]);
  const [diskPoliciesLoading, setDiskPoliciesLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const addNotification = (message, type) => {
    const id = Date.now() + Math.random();
    setNotifications((prev) => [...prev, { id, message, type }]);
  };

  const showSuccessNotification = (message) => addNotification(message, 'success');
  const showErrorNotification = (message) => addNotification(message, 'error');

  const authHeaders = () => ({
    Authorization: `Bearer ${authData.accessToken}`,
    'Content-Type': 'application/json',
  });

  // Backend errors arrive as ExceptionDTO {status, error, message}; keep the
  // message around so callers can show it instead of the bare status code.
  const readErrorMessage = async (response) => {
    try {
      const body = await response.json();
      return typeof body?.message === 'string' && body.message.trim() ? body.message : null;
    } catch (error) {
      return null;
    }
  };

  const request = async (path, options = {}) => {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      headers: authHeaders(),
      ...options,
    });
    if (!response.ok) {
      const error = new Error(`Request failed (${response.status})`);
      error.status = response.status;
      error.serverMessage = await readErrorMessage(response);
      throw error;
    }
    return response;
  };

  const requestJson = async (path, options = {}) => {
    const response = await request(path, options);
    return response.json();
  };

  // Header lists ({id, name}) are expanded into full modes via the per-id
  // endpoint; a mode whose detail fetch fails still shows up (entries: [])
  // so one bad record cannot blank the whole overview.
  const fetchModesWithEntries = async (basePath) => {
    const headers = await requestJson(basePath);
    return Promise.all(
      (headers || []).map(async (header) => {
        try {
          return await requestJson(`${basePath}/${header.id}`);
        } catch (error) {
          return { ...header, entries: [] };
        }
      })
    );
  };

  const fetchPaymentModes = async () => {
    if (!authData?.accessToken) return;
    setPaymentModesLoading(true);
    try {
      const modes = await fetchModesWithEntries('/paymentmodes');
      setPaymentModes(modes.sort(byName));
    } catch (error) {
      showErrorNotification(`Failed to fetch payment modes: ${error.message}`);
      setPaymentModes([]);
    } finally {
      setPaymentModesLoading(false);
    }
  };

  const createPaymentMode = async (paymentMode) => {
    if (!authData?.accessToken) return;
    try {
      const created = await requestJson('/paymentmodes', {
        method: 'POST',
        body: JSON.stringify(paymentMode),
      });
      setPaymentModes((prev) => [...prev, created].sort(byName));
      showSuccessNotification(`Payment mode "${created.name}" created.`);
      return created;
    } catch (error) {
      showErrorNotification(`Failed to create payment mode: ${error.message}`);
      throw error;
    }
  };

  const deletePaymentMode = async (paymentModeId) => {
    if (!authData?.accessToken) return;
    try {
      await request(`/paymentmodes/${paymentModeId}`, { method: 'DELETE' });
      setPaymentModes((prev) => prev.filter((mode) => mode.id !== paymentModeId));
      showSuccessNotification('Payment mode deleted.');
    } catch (error) {
      // DJB-178: the backend rejects the delete (400) while machines are
      // still subscribed to the mode — explain that instead of the bare
      // status code. Kept ≤50 chars so Notification shows it untruncated.
      if (error.status === 400 || error.status === 409) {
        showErrorNotification("Can't delete: machines are still subscribed.");
      } else {
        showErrorNotification(`Failed to delete payment mode: ${error.message}`);
      }
      throw error;
    }
  };

  // DJB-218: multipart upload of the price-sheet PNG. The browser must set
  // the multipart boundary itself, so the JSON Content-Type header is left
  // out on purpose. The backend validates the PNG signature and answers 400
  // with a message ("Price sheet must be a PNG image.") — surface that text.
  const uploadPriceSheet = async (paymentModeId, file) => {
    if (!authData?.accessToken) return;
    try {
      const formData = new FormData();
      formData.append('file', file, file.name || 'pricesheet.png');
      await request(`/paymentmodes/${paymentModeId}/pricesheet`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authData.accessToken}` },
        body: formData,
      });
      setPriceSheetVersions((prev) => ({ ...prev, [paymentModeId]: Date.now() }));
      showSuccessNotification('Price sheet uploaded.');
    } catch (error) {
      // Kept ≤50 chars so Notification shows it untruncated.
      showErrorNotification(
        error.serverMessage || `Price sheet upload failed: ${error.message}`
      );
      throw error;
    }
  };

  const fetchBonusConfigurations = async () => {
    if (!authData?.accessToken) return;
    setBonusConfigurationsLoading(true);
    try {
      const configurations = await requestJson('/bonusconfiguration');
      setBonusConfigurations((configurations || []).sort(byName));
    } catch (error) {
      showErrorNotification(`Failed to fetch bonus configurations: ${error.message}`);
      setBonusConfigurations([]);
    } finally {
      setBonusConfigurationsLoading(false);
    }
  };

  const createBonusConfiguration = async (configuration) => {
    if (!authData?.accessToken) return;
    try {
      const created = await requestJson('/bonusconfiguration', {
        method: 'POST',
        body: JSON.stringify(configuration),
      });
      setBonusConfigurations((prev) => [...prev, created].sort(byName));
      showSuccessNotification(`Bonus model "${created.name}" created.`);
      return created;
    } catch (error) {
      showErrorNotification(`Failed to create bonus model: ${error.message}`);
      throw error;
    }
  };

  const updateBonusConfiguration = async (configurationId, configuration) => {
    if (!authData?.accessToken) return;
    try {
      const updated = await requestJson(`/bonusconfiguration/${configurationId}`, {
        method: 'PUT',
        body: JSON.stringify(configuration),
      });
      setBonusConfigurations((prev) =>
        prev.map((existing) => (existing.id === configurationId ? updated : existing)).sort(byName)
      );
      showSuccessNotification(`Bonus model "${updated.name}" updated.`);
      return updated;
    } catch (error) {
      showErrorNotification(`Failed to update bonus model: ${error.message}`);
      throw error;
    }
  };

  const deleteBonusConfiguration = async (configurationId) => {
    if (!authData?.accessToken) return;
    try {
      await request(`/bonusconfiguration/${configurationId}`, { method: 'DELETE' });
      setBonusConfigurations((prev) => prev.filter((existing) => existing.id !== configurationId));
      showSuccessNotification('Bonus model deleted.');
    } catch (error) {
      showErrorNotification(`Failed to delete bonus model: ${error.message}`);
      throw error;
    }
  };

  const fetchLongTrackModes = async () => {
    if (!authData?.accessToken) return;
    setLongTrackModesLoading(true);
    try {
      const modes = await fetchModesWithEntries('/longtrackpricemodes');
      setLongTrackModes(modes.sort(byName));
    } catch (error) {
      showErrorNotification(`Failed to fetch long-track price models: ${error.message}`);
      setLongTrackModes([]);
    } finally {
      setLongTrackModesLoading(false);
    }
  };

  const createLongTrackMode = async (mode) => {
    if (!authData?.accessToken) return;
    try {
      const created = await requestJson('/longtrackpricemodes', {
        method: 'POST',
        body: JSON.stringify(mode),
      });
      setLongTrackModes((prev) => [...prev, created].sort(byName));
      showSuccessNotification(`Long-track price model "${created.name}" created.`);
      return created;
    } catch (error) {
      showErrorNotification(`Failed to create long-track price model: ${error.message}`);
      throw error;
    }
  };

  const deleteLongTrackMode = async (modeId) => {
    if (!authData?.accessToken) return;
    try {
      await request(`/longtrackpricemodes/${modeId}`, { method: 'DELETE' });
      setLongTrackModes((prev) => prev.filter((mode) => mode.id !== modeId));
      showSuccessNotification('Long-track price model deleted.');
    } catch (error) {
      showErrorNotification(`Failed to delete long-track price model: ${error.message}`);
      throw error;
    }
  };

  const fetchDiskPolicies = async () => {
    if (!authData?.accessToken) return;
    setDiskPoliciesLoading(true);
    try {
      const policies = await requestJson('/disk-policies');
      setDiskPolicies((policies || []).sort(byName));
    } catch (error) {
      showErrorNotification(`Failed to fetch disk policies: ${error.message}`);
      setDiskPolicies([]);
    } finally {
      setDiskPoliciesLoading(false);
    }
  };

  const createDiskPolicy = async (policy) => {
    if (!authData?.accessToken) return;
    try {
      const created = await requestJson('/disk-policies', {
        method: 'POST',
        body: JSON.stringify(policy),
      });
      setDiskPolicies((prev) => [...prev, created].sort(byName));
      showSuccessNotification(`Disk policy "${created.name}" created.`);
      return created;
    } catch (error) {
      showErrorNotification(`Failed to create disk policy: ${error.message}`);
      throw error;
    }
  };

  const updateDiskPolicy = async (policyId, policy) => {
    if (!authData?.accessToken) return;
    try {
      const updated = await requestJson(`/disk-policies/${policyId}`, {
        method: 'PUT',
        body: JSON.stringify(policy),
      });
      setDiskPolicies((prev) =>
        prev.map((existing) => (existing.id === policyId ? updated : existing)).sort(byName)
      );
      showSuccessNotification(`Disk policy "${updated.name}" updated.`);
      return updated;
    } catch (error) {
      showErrorNotification(`Failed to update disk policy: ${error.message}`);
      throw error;
    }
  };

  const deleteDiskPolicy = async (policyId) => {
    if (!authData?.accessToken) return;
    try {
      await request(`/disk-policies/${policyId}`, { method: 'DELETE' });
      setDiskPolicies((prev) => prev.filter((existing) => existing.id !== policyId));
      showSuccessNotification('Disk policy deleted.');
    } catch (error) {
      showErrorNotification(`Failed to delete disk policy: ${error.message}`);
      throw error;
    }
  };

  // The provider is mounted by the Configuration page itself, so a page visit
  // is what triggers the load (and a revisit refreshes).
  useEffect(() => {
    if (!authData?.accessToken) return;
    fetchPaymentModes();
    fetchBonusConfigurations();
    fetchLongTrackModes();
    fetchDiskPolicies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData?.accessToken]);

  return (
    <ConfigurationContext.Provider
      value={{
        paymentModes,
        paymentModesLoading,
        priceSheetVersions,
        bonusConfigurations,
        bonusConfigurationsLoading,
        longTrackModes,
        longTrackModesLoading,
        diskPolicies,
        diskPoliciesLoading,
        fetchPaymentModes,
        createPaymentMode,
        deletePaymentMode,
        uploadPriceSheet,
        fetchBonusConfigurations,
        createBonusConfiguration,
        updateBonusConfiguration,
        deleteBonusConfiguration,
        fetchLongTrackModes,
        createLongTrackMode,
        deleteLongTrackMode,
        fetchDiskPolicies,
        createDiskPolicy,
        updateDiskPolicy,
        deleteDiskPolicy,
      }}
    >
      {children}
      <div className="notification-container">
        {notifications.map((notification) => (
          <Notification
            key={notification.id}
            message={notification.message}
            type={notification.type}
            onClose={() =>
              setNotifications((prev) => prev.filter((n) => n.id !== notification.id))
            }
          />
        ))}
      </div>
    </ConfigurationContext.Provider>
  );
};

export default ConfigurationProvider;
